import { v4 as uuidv4 } from "uuid";

import { firebaseStorage } from "@rapid/utils/api/firebaseStorage";
import type { FrameInferenceResult, RapidBoxPromptData } from "@rapid/types/inference";
import type { RapidSetupPayloadRef } from "@rapid/types/workflow";

type RapidSetupPayload = {
    version: "1.0";
    instantPromptData: RapidBoxPromptData[];
    groundTruthData: FrameInferenceResult[];
    predictionDatasetData: FrameInferenceResult[];
    predictionReviewData: FrameInferenceResult[];
};

const RAPID_SETUP_PAYLOAD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const gzipBlobIfAvailable = async (
    blob: Blob
): Promise<{ blob: Blob; contentEncoding?: "gzip"; extension: string }> => {
    const CompressionStreamCtor = (globalThis as any).CompressionStream;

    if (!CompressionStreamCtor) {
        return { blob, extension: "json" };
    }

    try {
        const compressedStream = blob.stream().pipeThrough(new CompressionStreamCtor("gzip"));
        const compressedBlob = await new Response(compressedStream).blob();

        return { blob: compressedBlob, contentEncoding: "gzip", extension: "json.gz" };
    } catch (error) {
        console.warn("Failed to gzip rapid setup payload, uploading JSON instead", error);
        return { blob, extension: "json" };
    }
};

export const uploadRapidSetupPayload = async ({
    storageUploadDir,
    instantPromptData = [],
    groundTruthData = [],
    predictionDatasetData = [],
    predictionReviewData = []
}: {
    storageUploadDir: string;
    instantPromptData?: RapidBoxPromptData[];
    groundTruthData?: FrameInferenceResult[];
    predictionDatasetData?: FrameInferenceResult[];
    predictionReviewData?: FrameInferenceResult[];
}): Promise<RapidSetupPayloadRef> => {
    const payload: RapidSetupPayload = {
        version: "1.0",
        instantPromptData,
        groundTruthData,
        predictionDatasetData,
        predictionReviewData
    };
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    const { blob, contentEncoding, extension } = await gzipBlobIfAvailable(jsonBlob);
    const filename = `rapid-setup-payloads/${uuidv4()}.${extension}`;
    const file = new File([blob], filename, { type: "application/json" });
    const expiresAt = new Date(Date.now() + RAPID_SETUP_PAYLOAD_TTL_MS).toISOString();
    const uploadResult = await firebaseStorage.uploadFile(
        file,
        filename,
        storageUploadDir,
        undefined,
        { expiresAt, temporary: "true", purpose: "rapid-first-model-setup" }
    );

    return {
        path: uploadResult.filename,
        contentEncoding,
        contentType: "application/json",
        sizeBytes: blob.size,
        expiresAt
    };
};
